import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Formik } from 'formik'
import Select from 'react-select'
import { Column, ColumnProps, Row, RowProps } from '@components/kit/grid'
import Text, { TextProps } from '@components/kit/text'
import Card from '@components/kit/card'
import Box from '@components/kit/box'
import { ChatContext } from '../chat.web'
import { useGoalsStore } from '@store/Goals'
import {
  GoalTaskTypeBackendEnum,
  IGoalTask,
  IMemberGoal,
  TaskAction
} from '@store/Goals/types'
import moment from 'moment'
import { titleCase } from '@utils/string-formatters'
import Button from '@components/kit/button'
import { useNavigate } from 'react-router-dom'
import Loading from '@components/kit/loading'
import Modal from '@components/kit/modal'
import Form from '@components/kit/form'
import { colors, fonts } from '@theme'
import { TextFieldFormated } from '@components/formFields'
import {
  ColumnWrapper,
  HorizontalRule,
  LabelText,
  RowWrapper,
  ValueText
} from '@components/table'

/* -----------------------------------------------------------------------------
 * MemberGoals
 * -------------------------------------------------------------------------- */

type GoalTaskTypeItem = {
  label: string
  value: GoalTaskTypeBackendEnum
}

function MemberGoals() {
  const { userId } = useContext(ChatContext)
  const navigate = useNavigate()

  const [isAddCustomTaskModalOpen, setIsAddCustomTaskModalOpen] =
    useState(false)
  const [addTaskTitle, setAddTaskTitle] = useState('')
  const [addTaskDescription, setAddTaskDescription] = useState('')
  const [addTaskType, setAddTaskType] =
    useState<GoalTaskTypeBackendEnum | null>(null)
  const [isAddTaskTypeOpen, setIsAddTaskTypeOpen] = useState(false)
  const visibleTasks: Array<'active' | 'completed' | 'postponed' | 'ignored'> =
    [] //'active'] //, 'completed']

  const TaskTypesByTypeId: Record<GoalTaskTypeBackendEnum, GoalTaskTypeItem> = {
    [GoalTaskTypeBackendEnum.PERMANENT]: {
      label: 'Permanent',
      value: GoalTaskTypeBackendEnum.PERMANENT
    },
    [GoalTaskTypeBackendEnum.RECURRING]: {
      label: 'Recurring',
      value: GoalTaskTypeBackendEnum.RECURRING
    },
    [GoalTaskTypeBackendEnum.SIMPLE]: {
      label: 'Simple',
      value: GoalTaskTypeBackendEnum.SIMPLE
    }
  }

  const { getMemberGoals, sendTaskAction, addGoalTask } =
    useGoalsStore.getState()
  const [memberGoals, setMemberGoals] = useState<IMemberGoal | null>(null)
  const [isLoadingMemberGoals, setIsLoadingMemberGoals] =
    useState<boolean>(true)
  const [isSendingTaskAction, setIsSendingTaskAction] = useState<boolean>(false)
  const [isAddingTaskAction, setIsAddingTaskAction] = useState<boolean>(false)

  const loadMemberGoals = useCallback(async () => {
    setIsLoadingMemberGoals(true)
    setMemberGoals(await getMemberGoals(userId))
    setIsLoadingMemberGoals(false)
  }, [getMemberGoals, userId])

  useEffect(() => {
    loadMemberGoals()
  }, [loadMemberGoals])

  const handleCompleteTask = async (taskId: number) => {
    setIsSendingTaskAction(true)
    await sendTaskAction(userId, taskId, TaskAction.COMPLETE)
    setIsSendingTaskAction(false)
    // the backend needs a moment before the task plan reflects the action
    setTimeout(() => loadMemberGoals(), 500)
  }

  const handleAddTask = async () => {
    setIsAddingTaskAction(true)
    await addGoalTask(userId, {
      title: addTaskTitle,
      description: addTaskDescription,
      type: addTaskType!
    })
    setIsAddingTaskAction(false)
    setTimeout(() => setIsAddCustomTaskModalOpen(false), 1000)
    setTimeout(() => loadMemberGoals(), 500)
  }

  if (!memberGoals) {
    return null
  }

  return (
    <React.Fragment>
      <Row>
        <Text mt='24' ml='12' font='display.16.bold'>
          Member Goals
        </Text>
      </Row>
      <Box mt='08' flexDirection='row' gap='04'>
        {/* 
        // Removed the buttons for now as per the request
        <Button
          onPress={() => navigate(`/goal-actions/${userId}`)}
          size='fit'
          variant='tertiary'
        >
          All goal's actions
        </Button>
        <Button
          onPress={() => setIsAddCustomTaskModalOpen(true)}
          size='fit'
          variant='tertiary'
        >
          Add new action
        </Button> 
        */}
      </Box>
      <Card mt='08' px='00' py='00'>
        <Row width='100%'>
          <ColumnWrapper width='70%'>
            <LabelText>Current goal</LabelText>
            <ValueText>{memberGoals.name}</ValueText>
          </ColumnWrapper>
          <ColumnWrapper width='30%'>
            <LabelText>Goal register date</LabelText>
            <ValueText>
              {moment(memberGoals._created).format('MM/DD/YYYY')}
            </ValueText>
          </ColumnWrapper>
        </Row>
        <Row width='100%'>
          <ColumnWrapper width='100%'>
            <LabelText>Notes</LabelText>
            <ValueText>{memberGoals?.notes || '-'}</ValueText>
          </ColumnWrapper>
        </Row>
        {visibleTasks.map(
          taskStatus =>
            !!memberGoals.action_plan?.[taskStatus]?.length && (
              <React.Fragment>
                <HorizontalRule />
                <RowWrapper>
                  <LabelText>{`${titleCase(taskStatus)} actions`}</LabelText>
                </RowWrapper>
                {memberGoals.action_plan[taskStatus].map((task: IGoalTask) => {
                  const isActivePermanentTask =
                    taskStatus === 'active' && task.type === 'PERMANENT'
                  return (
                    <Row key={task.id} width='100%'>
                      <ColumnWrapper
                        minWidth={isActivePermanentTask ? '70%' : '100%'}
                        maxWidth={isActivePermanentTask ? '80%' : '100%'}
                        style={{ flexShrink: isActivePermanentTask ? 1 : 0 }}
                      >
                        <ValueText style={{ marginLeft: '1.5em' }}>{`• ${
                          task.title
                        } (${titleCase(task.type)})`}</ValueText>
                        {task?.description && task.description.length > 0 && (
                          <ValueText
                            style={{ marginLeft: '2.5em' }}
                            textColor='coolGray'
                          >{`${task.description}`}</ValueText>
                        )}
                      </ColumnWrapper>
                      {isActivePermanentTask && (
                        <ColumnWrapper
                          alignItems='flex-end'
                          width='fit-content'
                          paddingLeft='00'
                        >
                          <Button
                            size='fit'
                            variant='tertiary'
                            font='text.10.bold'
                            paddingLeft='04'
                            paddingRight='04'
                            onPress={() => handleCompleteTask(task.id)}
                          >
                            Mark as Completed
                          </Button>
                        </ColumnWrapper>
                      )}
                    </Row>
                  )
                })}
              </React.Fragment>
            )
        )}
        {(isSendingTaskAction || isLoadingMemberGoals) && (
          <Box
            position='absolute'
            width='100%'
            height='100%'
            top='0'
            left='0'
            right='0'
            flex='1'
            justifyContent='center'
            alignItems='center'
            style={{
              backgroundColor: '#f4f7facc'
            }}
          >
            <Loading size='large' />
          </Box>
        )}
      </Card>
      <Modal
        open={isAddCustomTaskModalOpen}
        // open
        onRequestClose={() => setIsAddCustomTaskModalOpen(false)}
        showCloseButton={true}
        borderRadius='medium'
        paddingTop='40'
      >
        <Text font='display.16.bold' textAlign='center'>
          New action
        </Text>
        <Formik initialValues={{}} onSubmit={() => {}} validateOnChange={true}>
          <Form>
            <Box
              flexDirection='column'
              gap='12'
              margin='12'
              alignItems='center'
            >
              <TextFieldFormated
                name='Title'
                label='Title'
                value={addTaskTitle}
                onChangeText={setAddTaskTitle}
                placeholder='Enter task title'
                width='20em'
              />
              <TextFieldFormated
                name='Description'
                label='Description'
                value={addTaskDescription}
                onChangeText={setAddTaskDescription}
                placeholder='Enter task description'
                multiline
                numberOfLines={4}
                maxLength={500}
                height='5.5em'
                width='100%'
              />
              <Box
                flexDirection='column-reverse'
                gap='16'
                width='100%'
                alignItems='center'
              >
                <Button
                  disabled={
                    !addTaskType ||
                    !addTaskTitle ||
                    !addTaskDescription ||
                    isAddingTaskAction
                  }
                  variant={
                    !addTaskType ||
                    !addTaskTitle ||
                    !addTaskDescription ||
                    isAddingTaskAction
                      ? 'gray'
                      : 'secondary'
                  }
                  width='fit-content'
                  onPress={handleAddTask}
                >
                  Add new action
                </Button>
                <Box
                  flexDirection='column'
                  gap='00'
                  backgroundColor='walrusBlue050'
                  width='100%'
                >
                  <Text
                    paddingTop='12'
                    paddingLeft='12'
                    font='display.12.bold'
                    textColor={
                      addTaskType != null || isAddTaskTypeOpen
                        ? ('#6670BC' as any)
                        : ('walrusBlue700' as any)
                    }
                  >
                    Task type
                  </Text>
                  <Select
                    placeholder={isAddTaskTypeOpen ? '' : 'Select action type'}
                    options={Object.values(TaskTypesByTypeId)}
                    onFocus={() => setIsAddTaskTypeOpen(true)}
                    onBlur={() => setIsAddTaskTypeOpen(false)}
                    onChange={(selectedOption: GoalTaskTypeItem | null) => {
                      if (selectedOption) {
                        setAddTaskType(selectedOption.value)
                      }
                    }}
                    // @ts-expect-error selectOption is an internal method not exposed in public props
                    selectOption={(selectedOption: GoalTaskTypeItem) => {
                      if (selectedOption) {
                        setAddTaskType(selectedOption.value)
                      }
                    }}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        fontFamily: fonts['display.14.bold'].fontFamily,
                        fontSize: fonts['display.14.bold'].fontSize,
                        fontWeight: fonts['display.14.bold'].fontWeight,
                        color: colors.walrusBlue700,
                        boxShadow: 'none',
                        border: 'none',
                        backgroundColor: colors.walrusBlue050,
                        width: '100%'
                      }),
                      menuList: (provided, state) => ({
                        ...provided,
                        border: '2px solid ' + colors.walrusBlue500
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        fontFamily: fonts['display.14.bold'].fontFamily,
                        fontSize: fonts['display.14.bold'].fontSize,
                        fontWeight: fonts['display.14.bold'].fontWeight,
                        color: state.isSelected
                          ? colors.walrusBlue500
                          : colors.walrusBlue700,
                        backgroundColor: state.isSelected
                          ? colors.walrusBlue050
                          : 'white'
                      }),
                      singleValue: (provided, state) => ({
                        ...provided,
                        fontFamily: fonts['display.14.bold'].fontFamily,
                        fontSize: fonts['display.14.bold'].fontSize,
                        fontWeight: fonts['display.14.bold'].fontWeight,
                        color: colors.walrusBlue700
                      })
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Form>
        </Formik>
        {isAddingTaskAction && (
          <Box
            position='absolute'
            width='100%'
            height='100%'
            top='0'
            left='0'
            right='0'
            flex='1'
            justifyContent='center'
            alignItems='center'
            style={{
              backgroundColor: '#f4f7facc'
            }}
          >
            <Loading size='large' />
          </Box>
        )}
      </Modal>
    </React.Fragment>
  )
}

export default MemberGoals
